import http from '@/lib/http'
import { skipToken, useQuery } from '@tanstack/react-query'
import { queryClient } from './queryClient'
import useMedRequestFiltering from '@/lib/api/med-requests/filtering'
import { useGetInvestigations } from './investigations/useGetInvestigations'
import { isUserTrialPrescriber } from '@/lib/permissions'
import { isMultiProductOrderMedRequest } from '@/lib/utils'
import type { MedRequest } from '@/types/data'
import { useFlag } from '@unleash/proxy-client-react'

const lockQueryKey = (id: string) => [`lock-${id}`]

/** Family rows of the opened request another user holds; deciding them would hit a 423 partway. */
export const useLockedFamilyIds = (id: string) =>
  useQuery<string[]>({ queryKey: lockQueryKey(id), queryFn: skipToken }).data ?? []

export const useLockItemById = (id: string, enabled: boolean, isInvestigation = false) => {
  const { data: medRequestsData } = useMedRequestFiltering(!isInvestigation)
  const medRequests = medRequestsData?.data ?? []

  const { data: investigations } = useGetInvestigations(
    {},
    {
      enabled: enabled && isInvestigation
    }
  )

  const isTrialPrescriber = isUserTrialPrescriber()
  const isMultiProductOrdersEnabled = useFlag('multi-product-orders')

  useQuery<string[]>({
    queryKey: lockQueryKey(id),
    refetchInterval: 1000 * 30,
    staleTime: 1000 * 10,
    refetchOnWindowFocus: 'always',
    refetchIntervalInBackground: true,
    queryFn: async () => {
      const items = isInvestigation ? investigations : medRequests
      const item = items?.find(obj => obj.id === id)

      if (isTrialPrescriber) return []

      const samePatientIds = item
        ? (items?.map(obj => (obj.patient.id === item.patient.id ? obj.id : null)) ?? []).filter(
            entry => entry !== null
          )
        : [id]

      // The opened request also locks its family (MPS siblings, other items of a multi product
      // order): the queue sweep above misses them, but the opened request's cache has them.
      const openedRequest = queryClient.getQueryData<MedRequest>(['medicationRequestById', id])
      const orderMedRequests =
        isMultiProductOrdersEnabled || !isMultiProductOrderMedRequest(openedRequest)
          ? (openedRequest?.orderMedRequests ?? [])
          : []
      const siblingIds = [...(openedRequest?.mpsMedRequests ?? []), ...orderMedRequests].map(row => row.id)

      const allIds = [...new Set([...samePatientIds, ...siblingIds])]

      const promiseItems = allIds.map(id => http.post<boolean, boolean>(`/prescriber/med-requests/${id}/lock`))

      const results = await Promise.all(promiseItems)
      const failedIds = allIds.filter((_, index) => !results[index])
      if (failedIds.length) {
        queryClient.invalidateQueries({ queryKey: ['medRequestFiltering'] })
        queryClient.invalidateQueries({
          predicate: query => query.queryKey.includes(id)
        })
      }
      return siblingIds.filter(siblingId => siblingId !== id && failedIds.includes(siblingId))
    },
    enabled: enabled && (isInvestigation ? !!investigations : !!medRequests)
  })
}
